import { useState, useMemo } from 'react';
import { DataGrid as MUIDataGrid } from '@mui/x-data-grid';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import iconLibrary from '../../../constans/icons';
import { trTR } from '@mui/x-data-grid/locales';
const DEFAULT_SIZES = {
  row: 30,
  header: 56,
  footer: 52,
  border: 2,
};

const DataGrid = ({
  columns,
  rows = [],
  rowCount,
  rowActions = [],
  onRowActionVisible,
  getRowId,
  loading = false,
  checkboxSelection = false,
  disableMultipleRowSelection = true,
  onRowSelect,
  paginationModel,
  onPaginationModelChange,
  pageSizeOptions = [5, 10, 25, 50, 100],
  enableSmartHeight = true,
  visibleRowsNoScroll = 10,
  rowHeight = DEFAULT_SIZES.row,
  columnHeaderHeight = DEFAULT_SIZES.header,
  footerHeight = DEFAULT_SIZES.footer,
  borderSize = DEFAULT_SIZES.border,
  containerStyle,
  sx: sxProp,

  ...rest
}) => {
  const [selectionModel] = useState([]);

  const CustomNoRowsOverlay = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Data bulunamadı
        </Typography>
      </Box>
    );
  };

  const actionColumn =
    rowActions.length > 0
      ? {
        field: '__actions__',
        headerName: 'Aksiyon',
        // Keep actions compact; assume up to 4 visible actions on a row
        width: rowActions.length === 1 ? 160 : Math.min(rowActions.length, 4) * 36 + 8,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const row = params.row;
          const visibleActions =
            typeof onRowActionVisible === 'function'
              ? rowActions.filter((action) => onRowActionVisible(row, action.name) !== false)
              : rowActions;

          if (visibleActions.length === 0) return null;

          return (
            <>
              {visibleActions.map((action, index) => (
                <Tooltip title={action.tooltip || action.label} key={index}>
                  <IconButton size="small" sx={{ p: 0.5 }} onClick={() => action.onClick?.(row)}>
                    {typeof action.icon === 'string'
                      ? iconLibrary[action.icon] || <span>{action.icon}</span>
                      : action.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </>
          );
        },
      }
      : null;

  const enhancedColumns = actionColumn ? [...columns, actionColumn] : columns;

  const gridHeight = useMemo(() => {
    const hasVariableHeight = Boolean(rest?.getRowHeight) || Boolean(rest?.autoHeight);
    if (!enableSmartHeight || hasVariableHeight) return undefined;
    const visible = Math.min(rows.length || 0, visibleRowsNoScroll);
    return columnHeaderHeight + footerHeight + borderSize + rowHeight * visible;
  }, [rows.length, visibleRowsNoScroll, rowHeight, columnHeaderHeight, footerHeight, borderSize, enableSmartHeight, rest]);

  return (
    <div style={{ height: rows.length === 0 ? 360 : gridHeight, width: '100%', minWidth: 0, ...(containerStyle || {}) }}>
      <MUIDataGrid
        rows={rows}
        rowCount={rowCount}
        getRowId={getRowId || ((row) => row.Id)}
        columns={enhancedColumns}
        loading={loading}
        checkboxSelection={checkboxSelection}
        disableMultipleRowSelection={disableMultipleRowSelection}
        selectionModel={selectionModel}
        paginationMode="server"
        paginationModel={paginationModel}
        localeText={trTR.components.MuiDataGrid.defaultProps.localeText}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={pageSizeOptions}
        rowHeight={rowHeight}
        columnHeaderHeight={columnHeaderHeight}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        sx={{
          // Default left alignment, and top-align all cell contents
          '& .MuiDataGrid-cell': { justifyContent: 'flex-start', alignItems: 'flex-start' },
          // Keep selection checkboxes centered vertically and horizontally
          '& .MuiDataGrid-cellCheckbox': { justifyContent: 'center', alignItems: 'center' },
          '& .MuiDataGrid-columnHeaderCheckbox': { justifyContent: 'center', alignItems: 'center' },
          '& .MuiDataGrid-cell .MuiDataGrid-cellContent': { alignSelf: 'flex-start' },
          '& .MuiDataGrid-cell--textRight': { justifyContent: 'flex-end' },
          '& .MuiDataGrid-cell--textCenter': { justifyContent: 'center' },
          '& .MuiDataGrid-columnHeaderTitle': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
          ...(sxProp || {}),
        }}
        {...rest}
      />
    </div>
  );
};

export default DataGrid;
